
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faClock, 
  faPhone, 
  faToggleOn, 
  faToggleOff,
  faExchangeAlt,
  faVolumeUp,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import AudioSelectorUploads from '../../Audio/AudioSelectorUploads';
import StasisFallbackInputs from '../../shared/StasisFallbackInputs';
import { isDestinationContext } from '../../../../services/destinationContexts';
import { integerInputValue, integerOrDefault, parseIntegerInput } from '../../../../services/integerInput';

function OverflowManager({
  overflows = [],
  onAdd,
  onRemove,
  onUpdate,
  availableEndpoints = [],
  availableQueues = [],
  availableContexts = [],
  availableTrunks = [],
  tenant,
  disabled = false,
  hasWhatsAppIntegration = false,
  userRole = ''
}) {

  const destinationTypes = [
    { value: 'endpoint', label: 'Ramal', icon: faPhone },
    { value: 'queue', label: 'Fila', icon: faExchangeAlt },
    { value: 'external', label: 'Externo', icon: faPhone },
    { value: 'disa', label: 'DISA/URA', icon: faPhone },
    { value: 'whatsapp', label: hasWhatsAppIntegration ? 'WhatsApp' : 'WhatsApp (sem integração)', icon: faPhone, disabled: !hasWhatsAppIntegration },
    { value: 'cycle', label: 'Ciclo (Reiniciar)', icon: faExchangeAlt },
    { value: 'hangup', label: 'Desligar', icon: faPhone },
    ...(userRole === 'root' ? [
      { value: 'stasis', label: 'Agent IA', icon: faPhone }
    ] : [])
  ];

  const audioTypes = [
    { value: 'ura', label: 'URA' },
    { value: 'msg', label: 'Mensagem' },
    { value: 'moh', label: 'Música de Espera' }
  ];

  const handleAdd = () => {
    if (onAdd) {
      onAdd();
    }
  };

  const handleRemove = (index) => {
    if (onRemove) {
      onRemove(index);
    }
  };

  const handleUpdate = (index, field, value) => {
    if (onUpdate) {
      onUpdate(index, field, value);
    }
  };

  const handleAudioSelect = (index, fileName, fileType, accountCode) => {
    handleUpdate(index, 'audio_file', fileName);
    handleUpdate(index, 'audio_type', fileType);
    handleUpdate(index, 'audio_accountcode', accountCode);
  };

  return (
    <div className="box">
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <h3 className="title is-5">
              <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
              Transbordos (Overflows)
            </h3>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <button
              type="button"
              className="button is-success is-small"
              onClick={handleAdd}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Adicionar Transbordo
            </button>
          </div>
        </div>
      </div>

      {overflows.length === 0 ? (
        <div className="notification is-info is-light">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          Nenhum transbordo configurado. Adicione transbordos para criar uma cadeia de encaminhamento quando o destino principal não atender.
        </div>
      ) : (
        <div>
          {overflows.map((overflow, index) => (
            <div key={index} className="box has-background-light mb-3">
              <div className="level mb-3">
                <div className="level-left">
                  <div className="level-item">
                    <span className="tag is-info is-medium">
                      Transbordo {index + 1}
                    </span>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <button
                      type="button"
                      className="button is-danger is-small"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                      title="Remover transbordo"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="columns">
                <div className="column is-4">
                  <div className="field">
                    <label className="label is-small">
                      <FontAwesomeIcon icon={faClock} className="mr-2" />
                      Timeout (segundos) *
                    </label>
                    <div className="control">
                      <input
                        type="number"
                        className="input is-small"
                        min="1"
                        max="300"
                        value={integerInputValue(overflow.timeout_seconds)}
                        onChange={(e) => handleUpdate(index, 'timeout_seconds', parseIntegerInput(e.target.value))}
                        onBlur={(e) => handleUpdate(index, 'timeout_seconds', integerOrDefault(e.target.value, 30))}
                        disabled={disabled}
                        placeholder="Ex: 20"
                      />
                    </div>
                    <p className="help">Tempo de espera antes de transbordar (1-300 segundos)</p>
                  </div>
                </div>

                <div className="column is-4">
                  <div className="field">
                    <label className="label is-small">
                      Tipo de Destino *
                    </label>
                    <div className="control">
                      <div className="select is-small is-fullwidth">
                        <select
                          value={overflow.destination_type || 'endpoint'}
                          onChange={(e) => handleUpdate(index, 'destination_type', e.target.value)}
                          disabled={disabled}
                        >
                          {destinationTypes.map(type => (
                            <option 
                              key={type.value} 
                              value={type.value}
                              disabled={type.disabled}
                            >
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-4">
                  {overflow.destination_type === 'endpoint' && (
                    <div className="field">
                      <label className="label is-small">Ramal (4 dígitos) *</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input is-small"
                          value={overflow.destination_overflow || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                            handleUpdate(index, 'destination_overflow', value);
                          }}
                          disabled={disabled}
                          placeholder="Ex: 1000, 2010"
                          maxLength={4}
                        />
                      </div>
                      <p className="help">Digite 4 dígitos numéricos</p>
                    </div>
                  )}

                  {overflow.destination_type === 'queue' && (
                    <div className="field">
                      <label className="label is-small">Fila *</label>
                      <div className="control">
                        <div className="select is-small is-fullwidth">
                          <select
                            value={overflow.destination_overflow || ''}
                            onChange={(e) => handleUpdate(index, 'destination_overflow', e.target.value)}
                            disabled={disabled}
                          >
                            <option value="">Selecione uma fila</option>
                            {availableQueues.map(queue => (
                              <option key={queue.name} value={queue.name}>
                                {queue.name} - {queue.displayname || 'Sem descrição'}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {overflow.destination_type === 'external' && (
                    <>
                      <div className="field">
                        <label className="label is-small">Número Externo *</label>
                        <div className="control">
                          <input
                            type="text"
                            className="input is-small"
                            value={overflow.destination_overflow || ''}
                            onChange={(e) => handleUpdate(index, 'destination_overflow', e.target.value)}
                            disabled={disabled}
                            placeholder="Ex: 11987654321"
                          />
                        </div>
                      </div>
                      
                      <div className="field">
                        <label className="label is-small">Tronco *</label>
                        <div className="control">
                          <div className="select is-small is-fullwidth">
                            <select
                              value={overflow.trunk_name || ''}
                              onChange={(e) => handleUpdate(index, 'trunk_name', e.target.value)}
                              disabled={disabled}
                            >
                              <option value="">Selecione o tronco</option>
                              {availableTrunks.map(trunk => (
                                <option key={trunk.name} value={trunk.name}>
                                  {trunk.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {overflow.destination_type === 'disa' && (
                    <div className="field">
                      <label className="label is-small">Contexto DISA *</label>
                      <div className="control">
                        <div className="select is-small is-fullwidth">
                          <select
                            value={overflow.disa_context || overflow.destination_overflow || ''}
                            onChange={(e) => {
                              handleUpdate(index, 'disa_context', e.target.value);
                              handleUpdate(index, 'destination_overflow', e.target.value);
                            }}
                            disabled={disabled}
                          >
                            <option value="">Selecione um contexto</option>
                            {availableContexts.filter(context => {
                              const contextName = (context.name || '').toLowerCase();
                              return isDestinationContext(contextName);
                            }).map(context => (
                              <option key={context.name} value={context.name}>
                                {context.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="help">Selecione o contexto DISA/URA</p>
                    </div>
                  )}

                  {overflow.destination_type === 'whatsapp' && (
                    <div className="field">
                      <label className="label is-small">WhatsApp</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input is-small"
                          value="Integração WhatsApp"
                          disabled
                          readOnly
                        />
                      </div>
                      <p className="help">Encaminha para integração WhatsApp</p>
                    </div>
                  )}

                  {overflow.destination_type === 'cycle' && (
                    <div className="field">
                      <label className="label is-small">Ciclo</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input is-small"
                          value={overflow.destination_overflow || 'CYCLE_RESTART'}
                          disabled
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <p className="help">Reinicia o fluxo de chamadas do início</p>
                    </div>
                  )}

                  {overflow.destination_type === 'hangup' && (
                    <div className="field">
                      <label className="label is-small">Desligar</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input is-small"
                          value="Desligar chamada"
                          disabled
                          readOnly
                        />
                      </div>
                      <p className="help">Desliga a chamada</p>
                    </div>
                  )}

                  {overflow.destination_type === 'stasis' && (
                    <div className="field">
                      <label className="label is-small">Agent IA</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input is-small"
                          value="voice-gateway-ai (fixo)"
                          disabled
                          readOnly
                        />
                      </div>
                      <StasisFallbackInputs
                        fallbackType={overflow.stasis_fallback_type || ''}
                        fallbackDestination={overflow.stasis_fallback_destination || ''}
                        onChange={(field, value) => handleUpdate(index, field, value)}
                        availableEndpoints={availableEndpoints}
                        availableQueues={availableQueues}
                        availableContexts={availableContexts}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="box has-background-white-ter">
                <div className="field">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={overflow.audio_enabled || false}
                      onChange={(e) => handleUpdate(index, 'audio_enabled', e.target.checked)}
                      disabled={disabled}
                    />
                    <span className="ml-2">
                      <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
                      Reproduzir áudio antes deste transbordo
                    </span>
                  </label>
                </div>

                {overflow.audio_enabled && (
                  <div className="columns">
                    <div className="column is-6">
                      <AudioSelectorUploads
                        selectedFile={overflow.audio_file || ''}
                        selectedType={overflow.audio_type || 'ura'}
                        accountCode={tenant}
                        onSelect={(fileName, fileType, accountCode) => 
                          handleAudioSelect(index, fileName, fileType, accountCode)
                        }
                        disabled={disabled}
                      />
                    </div>
                    <div className="column is-6">
                      <div className="field">
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={overflow.hangup_after_audio || false}
                            onChange={(e) => handleUpdate(index, 'hangup_after_audio', e.target.checked)}
                            disabled={disabled}
                          />
                          <span className="ml-2">Desligar após reproduzir áudio</span>
                        </label>
                        <p className="help">Se marcado, a chamada será desligada após o áudio</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="field mt-3">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={overflow.active !== false}
                    onChange={(e) => handleUpdate(index, 'active', e.target.checked)}
                    disabled={disabled}
                  />
                  <span className="ml-2">
                    <FontAwesomeIcon 
                      icon={overflow.active !== false ? faToggleOn : faToggleOff} 
                      className="mr-2"
                    />
                    Transbordo ativo
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OverflowManager;

